import { Link } from 'react-router-dom';
import { ThemeToggle } from '@doccareplus/ui';

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

interface LegalPageLayoutProps {
  title: string;
  effectiveDate: string;
  intro: string;
  sections: LegalSection[];
}

/**
 * Shared reading layout for standalone legal documents (Privacy Policy, Terms
 * of Service). Renders a branded header, numbered sections, and cross-links.
 */
export function LegalPageLayout({ title, effectiveDate, intro, sections }: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <ThemeToggle className="fixed top-4 right-4 z-50 shadow-[var(--shadow-1)]" />

      <header className="border-b border-[var(--border)] bg-[var(--surface)]">
        <div className="mx-auto w-full max-w-3xl px-6 py-5">
          <Link to="/" className="inline-flex items-center hover:no-underline">
            <img src="/long_logo.svg" alt="DocCarePlus" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-6 py-12">
        <h1 className="text-3xl font-bold tracking-tight text-[var(--text)]">{title}</h1>
        <p className="mt-2 text-[13px] text-[var(--text-3)]">Last updated: {effectiveDate}</p>
        <p className="mt-6 text-[15px] leading-relaxed text-[var(--text-2)]">{intro}</p>

        <div className="mt-10 flex flex-col gap-10">
          {sections.map((section, i) => (
            <section key={section.heading}>
              <h2 className="text-lg font-semibold text-[var(--text)]">
                {i + 1}. {section.heading}
              </h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mt-3 text-[15px] leading-relaxed text-[var(--text-2)]">
                  {paragraph}
                </p>
              ))}
              {section.bullets && (
                <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-[15px] leading-relaxed text-[var(--text-2)]">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <footer className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--border)] pt-8 text-sm">
          <Link to="/" className="font-semibold text-[var(--primary)] hover:underline">
            &larr; Back to home
          </Link>
          <div className="flex gap-6 text-[var(--text-3)]">
            <Link to="/privacy" className="hover:text-[var(--text)] hover:underline">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-[var(--text)] hover:underline">
              Terms of Service
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
